import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'

export interface RoomResponse {
  roomId: string
  roomName: string
  categoryType: string
  description?: string
  eventDatetime: string | null
  expirationDate: string | null
  maxParticipants: number
  status: string
  createdAt: string | null
  participantCount: number
  canEdit?: boolean
}

export interface RoomDetailResponse {
  roomId: string
  roomName: string
  categoryType: string
  eventDatetime: string | null
  maxParticipants: number
  createdUserId: string
  expirationDate: string | null
  status: string
  createdAt: string | null
  description?: string
  password?: string
}

export interface RoomSubInfoResponse {
  roomSubInfoId: string
  roomId: string
  propertyName: string
  propertyType: 'text' | 'number' | 'date' | 'checkbox'
  required: boolean
  displayOrder: number
}

export interface UserRoomSubInfoResponse {
  roomSubInfoId: string
  subValue: string | null
}

export interface ProfileResponse {
  userId: string
  eventType: string
  prText: string
  isPrPublic: boolean
  isEmailPublic: boolean
  isTelPublic: boolean
  mailAddress: string | null
  username: string | null
  /** このルームの作成者(主催者)かどうか */
  isRoomOwner?: boolean
  bizName?: string
  bizCompanyName?: string
  bizPosition?: string
  bizContent?: string
  bizComment?: string
  bizTargetIndustry?: string
  bizExcludeIndustry?: string
  bizEmail?: string
  bizTel?: string
  loveNickname?: string
  loveAge?: number
  loveJob?: string
  loveGender?: string
  loveAnnualIncome?: string
  loveWantsKids?: string
  loveTargetPerson?: string
  loveExcludePerson?: string
  loveContactInfo?: string
  loveTel?: string
  careerName?: string
  careerPrevCompany?: string
  careerLastPosition?: string
  careerEducationLevel?: string
  careerSchoolName?: string
  careerAge?: number
  careerGender?: string
  careerComment?: string
  careerTargetCompany?: string
  careerExcludeConditions?: string
  careerEmail?: string
  careerTel?: string
}

export const getRooms = async (userId?: string): Promise<RoomResponse[]> => {
  try {
    const getRoomsFunction = httpsCallable(functions, 'get_rooms')
    const result = await getRoomsFunction(userId ? { userId } : {})
    const data = result.data as { rooms: RoomResponse[] }
    return data.rooms
  } catch (error) {
    console.error('[getRooms] Error:', error)
    throw error
  }
}

export const saveRoom = async (request: {
  roomId?: string
  roomName: string
  categoryType: string
  description?: string
  eventDatetime?: string
  expirationDate: string
  password: string
  maxParticipants: number
}) => {
  if (request.roomId) {
    return updateRoom({
      roomId: request.roomId,
      roomName: request.roomName,
      categoryType: request.categoryType,
      description: request.description,
      eventDatetime: request.eventDatetime,
      expirationDate: request.expirationDate,
      password: request.password,
      maxParticipants: request.maxParticipants,
    })
  }
  return createRoom({
    roomName: request.roomName,
    categoryType: request.categoryType,
    description: request.description,
    eventDatetime: request.eventDatetime,
    expirationDate: request.expirationDate,
    password: request.password,
    maxParticipants: request.maxParticipants,
  })
}

export const getRoom = async (roomId: string): Promise<RoomDetailResponse> => {
  try {
    const getRoomFunction = httpsCallable(functions, 'get_room')
    const result = await getRoomFunction({ roomId })
    return result.data as RoomDetailResponse
  } catch (error) {
    console.error('[getRoom] Error:', error)
    throw error
  }
}

export const getRoomProfiles = async (roomId: string): Promise<ProfileResponse[]> => {
  try {
    const getRoomProfilesFunction = httpsCallable(functions, 'get_room_profiles')
    const result = await getRoomProfilesFunction({ roomId })
    const data = result.data as { profiles: ProfileResponse[] }
    return data.profiles
  } catch (error) {
    console.error('[getRoomProfiles] Error:', error)
    throw error
  }
}

export const joinRoom = async (roomId: string, password: string) => {
  try {
    const joinRoomFunction = httpsCallable(functions, 'join_room')
    const result = await joinRoomFunction({ roomId, password })
    return result.data as { success: boolean }
  } catch (error) {
    console.error('[joinRoom] Error:', error)
    throw error
  }
}

export const createRoom = async (request: {
  roomName: string
  categoryType: string
  description?: string
  eventDatetime?: string
  expirationDate: string
  password: string
  maxParticipants: number
}) => {
  try {
    const createRoomFunction = httpsCallable(functions, 'create_room')
    const result = await createRoomFunction(request)
    return result.data
  } catch (error) {
    console.error('[createRoom] Error:', error)
    throw error
  }
}

export const updateRoom = async (request: {
  roomId: string
  roomName: string
  categoryType: string
  description?: string
  eventDatetime?: string
  expirationDate: string
  password: string
  maxParticipants: number
}) => {
  try {
    const updateRoomFunction = httpsCallable(functions, 'update_room')
    const result = await updateRoomFunction(request)
    return result.data
  } catch (error) {
    console.error('[updateRoom] Error:', error)
    throw error
  }
}

export const deleteRoom = async (roomId: string) => {
  try {
    const deleteRoomFunction = httpsCallable(functions, 'delete_room')
    const result = await deleteRoomFunction({ roomId })
    return result.data as { success: boolean; roomId: string }
  } catch (error) {
    console.error('[deleteRoom] Error:', error)
    throw error
  }
}

export const createRoomSubInfo = async (request: {
  roomId: string
  propertyName: string
  propertyType: string
  required?: boolean
  displayOrder?: number
}) => {
  try {
    const createRoomSubInfoFunction = httpsCallable(functions, 'create_room_sub_info')
    const result = await createRoomSubInfoFunction(request)
    return result.data
  } catch (error) {
    console.error('[createRoomSubInfo] Error:', error)
    throw error
  }
}

export const updateRoomSubInfo = async (request: {
  roomSubInfoId: string
  propertyName: string
  propertyType: string
  required?: boolean
  displayOrder?: number
}) => {
  try {
    const updateRoomSubInfoFunction = httpsCallable(functions, 'update_room_sub_info')
    const result = await updateRoomSubInfoFunction(request)
    return result.data
  } catch (error) {
    console.error('[updateRoomSubInfo] Error:', error)
    throw error
  }
}

export const getRoomSubInfos = async (roomId: string): Promise<RoomSubInfoResponse[]> => {
  try {
    const getRoomSubInfosFunction = httpsCallable(functions, 'get_room_sub_infos')
    const result = await getRoomSubInfosFunction({ roomId })
    const data = result.data as { subInfos: RoomSubInfoResponse[] }
    return data.subInfos
  } catch (error) {
    console.error('[getRoomSubInfos] Error:', error)
    throw error
  }
}

export const getUserRoomSubInfos = async (roomId: string): Promise<UserRoomSubInfoResponse[]> => {
  try {
    const getUserRoomSubInfosFunction = httpsCallable(functions, 'get_user_room_sub_infos')
    const result = await getUserRoomSubInfosFunction({ roomId })
    const data = result.data as { subInfos: UserRoomSubInfoResponse[] }
    return data.subInfos
  } catch (error) {
    console.error('[getUserRoomSubInfos] Error:', error)
    throw error
  }
}

export const deleteRoomSubInfo = async (roomSubInfoId: string) => {
  try {
    const deleteRoomSubInfoFunction = httpsCallable(functions, 'delete_room_sub_info')
    const result = await deleteRoomSubInfoFunction({ roomSubInfoId })
    return result.data
  } catch (error) {
    console.error('[deleteRoomSubInfo] Error:', error)
    throw error
  }
}

export const saveRoomSubInfos = async (request: {
  roomId: string
  subInfos: Array<{ roomSubInfoId: string; subValue: string | number | boolean }>
}) => {
  try {
    const saveRoomSubInfosFunction = httpsCallable(functions, 'save_room_sub_infos')
    const result = await saveRoomSubInfosFunction(request)
    return result.data as { savedCount: number }
  } catch (error) {
    console.error('[saveRoomSubInfos] Error:', error)
    throw error
  }
}
